import { useKohdeSynkka } from "./Kohdekonteksti";
import { Ohje } from "./Ohje";
import { useCallback, useEffect, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { api, ApiError, type Kayttaja, type LaiteRivi, type SeuraavaLiitos } from "./api";
import LiitosKierto from "./LiitosKierto";
import LaiteLuonti from "./LaiteLuonti";
import LaiteSelailu from "./LaiteSelailu";
import { KohdeValinta } from "./KohdeValinta";
import { KohdeLinkki } from "./Linkit";
import { useOsoiteHaku, lueHaku, haunLuku } from "./osoitehaku";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Laiteasennus — asentajan polku.

   Momenttikirjauksella on kaksi eri käyttäjää eikä yksi.

   Työnjohto valmistelee liitoslistan etukäteen ja korjaa sitä
   jälkikäteen; se on Excel-kierto. Asentaja seisoo koneen vieressä
   momenttiavain kädessä eikä avaa Exceliä — hän merkitsee liitoksen
   puhelimesta, yksi painallus kerrallaan.

   Tämä näkymä on asentajan polku, ja se rakennettiin ensin koska se
   on se jota käytetään joka päivä työmaalla.

   Suunnitteluehdot jotka seuraavat siitä:

     · yksi verkkokutsu per painallus — kuittaus palauttaa seuraavan
       samassa vastauksessa, koska työmaan verkko on epävarma
     · vaadittu momentti on näytön suurin asia, ei kenttä lomakkeella
     · ei vahvistusikkunaa — MUTTA se perustelu nojaa ehtoon, ja ehto
       nimetään ehdoksi: peruminen on oltava olemassa ja yhtä näkyvä
       kuin kuittaus. Ensimmäisessä versiossa sitä ei ollut, jolloin
       turvaverkko oli korvattu lupauksella. Nyt "Peru" on samalla
       rivillä kuin kuittauksen vahvistus. Jos se joskus poistetaan,
       vahvistusikkuna on palautettava.
     · edistymä näkyy koko ajan, koska seuraava kysymys työmaalla on
       aina "montako on jäljellä"
   ═══════════════════════════════════════════════════════════ */

function nm(arvo: string | number | null): string {
  if (arvo === null || arvo === "") return "—";
  const n = typeof arvo === "string" ? Number(arvo) : arvo;
  return Number.isFinite(n) ? `${n} Nm` : "—";
}

export default function Laiteasennus({ kayttaja, osoiteHaku, asetaOsoiteHaku }: {
  kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (haku: string) => void;
}) {
  const [laitteet, setLaitteet] = useState<LaiteRivi[] | null>(null);
  const [valittu, setValittu] = useState<number | null>(null);
  const [tila, setTila] = useState<SeuraavaLiitos | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kirjaa, setKirjaa] = useState(false);
  const [huomio, setHuomio] = useState("");
  // Viimeksi kuitattu kantaa id:n, koska peruminen kohdistuu siihen
  // eikä "edelliseen" jota palvelin ei tiedä.
  const [viimeksi, setViimeksi] = useState<{ id: number; tunnus: string } | null>(null);
  /* KOHDESUODATIN. Joka muu Tuotanto-näkymä (Selailu, Etenemä,
     Varasto, Ainestodistukset) alkaa KOHDE-valinnalla; tämä ei, ja
     laitteet lueteltiin kaikista kohteista peräkkäin. Yhdellä
     kohteella se on sama asia, usealla se on eri asia — eikä
     esittelijä voi avata kohdetta siitä paikasta josta muutkin.

     Valinta johdetaan LAITTEISTA eikä `/api/kohteet`:sta: kohde
     jolla ei ole yhtään laitetta ei ole tässä näkymässä valinta
     vaan tyhjä ruutu. Sama peruste kuin Hubin oletuskohteella —
     valinta tarjotaan sieltä missä sisältöä on. */
  const [kohdeSuodatin, setKohdeSuodatin] = useState<number | "">(() => haunLuku(lueHaku(osoiteHaku), "kohde"));
  /* 59: kohde on osoitteessa (`#/laiteasennus?kohde=<id>`), jotta kirjausnäkymän otsikon linkki
     «Kohde N-2404» vie kohteen laitelistaan ja osoite on jaettavissa — sama mekanismi kuin Selailussa. */
  useOsoiteHaku(osoiteHaku, asetaOsoiteHaku, { kohde: kohdeSuodatin },
    (p) => { setKohdeSuodatin(haunLuku(p, "kohde")); });
  // T426: yläpalkin kohdevalinta ohjaa; osoitteen ?kohde= vaihtaa yläpalkin.
  const { ylapalkissa } = useKohdeSynkka(kohdeSuodatin, setKohdeSuodatin,
    { osoitteenKohde: haunLuku(lueHaku(osoiteHaku), "kohde"), kaikkiSallittu: true });

  const paivitaLista = useCallback(async () => {
    try {
      setLaitteet(await api.laitteet());
    } catch (v) {
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : String(v));
    }
  }, []);

  useEffect(() => { void paivitaLista(); }, [paivitaLista]);

  const saaLuoda = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));

  const uusi = useViimeisin();   // päätös 99: «Kuittaa» osuu näytettyyn liitokseen — vain viimeisimmän laitteen vastaus
  const lataa = useCallback(async (id: number) => {
    const tuore = uusi();
    setVirhe(null);
    try {
      const t = await api.laiteSeuraava(id);
      if (!tuore()) return;
      setTila(t);
    } catch (v) {
      if (!tuore()) return;
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : String(v));
    }
  }, []);

  useEffect(() => { if (valittu !== null) void lataa(valittu); }, [valittu, lataa]);

  /**
   * Kuittauksen peruminen.
   *
   * Tämä on se turvaverkko jonka varassa vahvistusikkunan poisjättö
   * lepää. Se on samalla ruudulla ja samankokoisena tekstinä kuin
   * kuittauksen vahvistus — peruminen ei saa olla piilotetumpi kuin
   * se teko jota se korjaa.
   */
  async function peru() {
    if (!viimeksi || kirjaa) return;
    setKirjaa(true);
    setVirhe(null);
    try {
      await api.peruLiitosKuittaus(viimeksi.id);
      setViimeksi(null);
      if (valittu !== null) setTila(await api.laiteSeuraava(valittu));
    } catch (v) {
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : String(v));
    } finally {
      setKirjaa(false);
    }
  }

  async function kuittaa(liitosId: number) {
    if (kirjaa) return;          // kaksoispainallus työmaalla on sääntö, ei poikkeus
    setKirjaa(true);
    setVirhe(null);
    try {
      const v = await api.kuittaaLiitos(liitosId, huomio.trim() ? { huomiot: huomio.trim() } : {});
      setViimeksi(v.kuitattu);
      setHuomio("");
      setTila(v);
    } catch (v) {
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : String(v));
    } finally {
      setKirjaa(false);
    }
  }

  if (virhe && !laitteet) return <p className="virhe">{virhe}</p>;
  if (!laitteet) return <p>{t("yleinen.ladataan")}</p>;

  /* ── Laitteen valinta ─────────────────────────────────── */
  if (valittu === null) {
    return (
      <section className="nakyma">
        {/* Talon otsikkokaava: silmu + otsikko. Ensimmäinen versio
            kirjoitti pelkän <h1>:n ja jäi kaavan ulkopuolelle. */}
        <header className="head">
          <div className="head__rivi"><h1>{t("la.otsikko")}</h1></div>
        </header>

        {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/laitteet (luonti ja Excel-tuonti työnjohdolle), /api/liitokset/:id/kuittaus (vaadi + henkilö), /api/vaiheketjut; kenttätekijälle laiteasennustyön työlaji. */}
        <Ohje
          kuka={<>{t("la.ohje.kuka")}</>}
          luvut={<>{t("la.ohje.luvut")}</>} />
        {/* Luonti on työnjohdon työtä. Ilman tätä moduulin sai päälle
            mutta ensimmäisen laitteen vain SQL:llä. */}
        {/* Tyhjän tilan teksti ENNEN lomaketta, ja eri teksti sille joka
            voi toimia. Aiemmin tässä luki "Työnjohto lisää laitteet"
            myös työnjohdolle itselleen, lomakkeen ALAPUOLELLA — ohje
            puhui kolmannessa persoonassa seisoen sen edessä joka voi
            tehdä sen. Sama vika kuin ohje joka osoittaa nappiin jota
            ei ole, vain toisin päin. */}
        {laitteet.length === 0 && (
          <p className="tyhja" data-sisalto="">
            {saaLuoda ? t("la.tyhja_luoja") : t("la.tyhja")}
          </p>
        )}
        {/* 59 (Toni 2.10.2026 «laiteasennus ei oo työkohteittain»): laite on kannassa
            kohdekohtainen (kohde_id NOT NULL), puute oli näkymässä. Lista ryhmitellään
            kohteittain, ja kohteen otsikossa on laitteiden ja vaiheiden valmius. Valitsin
            on sama komponentti kuin Hitsauslokissa, ja se piirretään vain kun kohteita on
            enemmän kuin yksi — yhden vaihtoehdon pudotusvalikko on koriste. */}
        {(() => {
          const ryhmat = [...laitteet.reduce((m, l) => {
            const r = m.get(l.kohde_id) ?? { id: l.kohde_id, tunnus: l.kohde_tunnus, nimi: l.kohde, laitteet: [] as LaiteRivi[] };
            r.laitteet.push(l); m.set(l.kohde_id, r); return m;
          }, new Map<number, { id: number; tunnus: string; nimi: string; laitteet: LaiteRivi[] }>()).values()]
            .sort((a, b) => a.tunnus.localeCompare(b.tunnus, "fi"));
          const nakyvat = ryhmat.filter((r) => kohdeSuodatin === "" || r.id === kohdeSuodatin);
          const summa = (rivit: LaiteRivi[], avain: "vaiheita" | "vaiheita_kuitattu" | "liitoksia" | "liitoksia_kuitattu") =>
            rivit.reduce((s, l) => s + l[avain], 0);
          return (
            <>
              {ryhmat.length >= 2 && !ylapalkissa && (
                <div className="suodatinrivi">
                  <KohdeValinta arvo={kohdeSuodatin} muuta={setKohdeSuodatin} kaikki={t("pu.kaikki_kohteet")}
                                kohteet={ryhmat.map((r) => ({ id: r.id, tunnus: r.tunnus, nimi: r.nimi, lisa: t("la.n_laitetta", { n: r.laitteet.length }) }))} />
                </div>
              )}
              {saaLuoda && <LaiteLuonti valmis={() => void paivitaLista()} />}
              {/* Toni 4.11.2026 klo 17.05 kohta 2: laitteita on, mutta ei valitulla kohteella — ennen tätä näkymä ei piirtänyt mitään
                  (kehityspalvelimen kävely 4.11.: taitemittaus ei löytänyt sisältöä). */}
              {laitteet.length > 0 && nakyvat.length === 0 && (
                <p className="tyhja" data-sisalto="" data-laitteet-tyhja-kohde="">{t("la.tyhja_kohde")}</p>
              )}
              {nakyvat.map((r) => (
                <section key={r.id} className="laiteryhma" data-laiteryhma={r.id} data-laitteita={r.laitteet.length}
                         data-vaiheet={`${summa(r.laitteet, "vaiheita_kuitattu")}/${summa(r.laitteet, "vaiheita")}`}>
                  <h2 className="laiteryhma__otsikko">
                    <KohdeLinkki tunnus={r.tunnus} /> {r.nimi}
                    <span className="dim">
                      {" · "}{r.laitteet.length === 1 ? t("la.yksi_laite") : t("la.n_laitetta", { n: r.laitteet.length })}
                      {` · ${t("la.vaiheet")} `}{summa(r.laitteet, "vaiheita_kuitattu")}/{summa(r.laitteet, "vaiheita")}
                      {` · ${t("la.liitokset")} `}{summa(r.laitteet, "liitoksia_kuitattu")}/{summa(r.laitteet, "liitoksia")}
                    </span>
                  </h2>
                  <ul className="laitelista" data-sisalto="">
                    {r.laitteet.map((l) => {
                      const jaljella = l.liitoksia - l.liitoksia_kuitattu;
                      return (
                        <li key={l.id}>
                          <button className="laitekortti" onClick={() => setValittu(l.id)}>
                            <span className="laitekortti-nimi">
                              {l.tunnus ? `${l.tunnus} · ` : ""}{l.valmistaja} {l.malli}
                            </span>
                            <span className="laitekortti-kohde">{l.sijainti ?? ""}</span>
                            <span className="laitekortti-edistyma">
                              {/* Johdettu luku, ei tallennettu valmiusprosentti. */}
                              {l.liitoksia === 0
                                ? t("la.ei_liitoslistaa")
                                : t("la.edistyma", { k: l.liitoksia_kuitattu, n: l.liitoksia, j: jaljella })}
                              {l.vaiheita > 0 && ` · ${t("la.vaiheet")} ${l.vaiheita_kuitattu}/${l.vaiheita}`}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </>
          );
        })()}
      </section>
    );
  }

  /* ── Kirjaus ──────────────────────────────────────────── */
  const laite = laitteet.find((l) => l.id === valittu);
  const tyonjohto = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  return (
    <section className="nakyma kirjaus">
      <button className="takaisin" onClick={() => { setValittu(null); setTila(null); setViimeksi(null); }}>
        ← {t("la.laitteet")}
      </button>
      <header className="head">
        <div className="eyebrow">{t("valikko.laiteasennus")} · {t("la.kirjaus")}</div>
        <div className="head__rivi">
          {/* 59: otsikossa kohde, ja linkki vie kohteen laitelistaan (osoitteen kohde). */}
          <h1 data-laite-kohde={laite?.kohde_id ?? ""}>
            {laite && (
              <><a href={`#/laiteasennus?kohde=${laite.kohde_id}`} className="hub__linkki" data-laite-kohdelinkki=""
                   onClick={() => { setKohdeSuodatin(laite.kohde_id); setValittu(null); setTila(null); setViimeksi(null); }}>
                {t("pu.kohde")} {laite.kohde_tunnus}
              </a>{" › "}</>
            )}
            {laite?.tunnus ? `${laite.tunnus} · ` : ""}{laite?.valmistaja} {laite?.malli}
          </h1>
        </div>
      </header>

      {virhe && <p className="virhe">{virhe}</p>}
      {!tila && <p>{t("yleinen.ladataan")}</p>}

      {/* Työnjohdon polku on eri kuin asentajan: Excel-kierto näkyy vain
          niille jotka valmistelevat listan. Asentaja ei avaa Exceliä,
          eikä hänen ruudulleen kuulu nappia jota hän ei käytä.
          Palvelin valvoo saman rajan vaadiRooli()-tarkistuksella. */}
      {tyonjohto && valittu !== null && (
        <LiitosKierto laiteId={valittu} valmis={() => void lataa(valittu)} />
      )}

      {/* Selailu: mitkä 14, ei vain 13/14. Kaikille — asentajakin
          hyötyy siitä että näkee mitkä ovat auki. */}
      {valittu !== null && (
        <LaiteSelailu laiteId={valittu} tyonjohto={tyonjohto}
                      muuttui={() => { void lataa(valittu); void paivitaLista(); }} />
      )}

      {tila && (
        <>
          <p className="edistyma">
            <strong>{tila.kuitattuja}/{tila.yhteensa}</strong> {t("la.liitosta_kuitattu")}
            {tila.jaljella > 0 && <> · <strong>{tila.jaljella}</strong> {t("la.jaljella")}</>}
          </p>
          {viimeksi && (
            <p className="kuittaus-ok">
              <span>✓ {t("la.kuitattu", { tunnus: viimeksi.tunnus })}</span>
              <button className="peru" onClick={() => void peru()} disabled={kirjaa}>
                {t("qr.peru")}
              </button>
            </p>
          )}

          {tila.yhteensa === 0 && (
            <p className="tyhja">
              {t("la.ei_listaa_selite")}
            </p>
          )}

          {tila.yhteensa > 0 && !tila.seuraava && (
            <p className="valmis">
              {t("la.kaikki_kuitattu", { n: tila.yhteensa })}
            </p>
          )}

          {tila.seuraava && (
            <div className="liitoskortti">
              <div className="liitos-tunnus">{tila.seuraava.tunnus}</div>
              {tila.seuraava.kuvaus && <div className="liitos-kuvaus">{tila.seuraava.kuvaus}</div>}
              {/* Vaadittu momentti on näytön suurin asia: se on se luku
                  jonka mukaan avain asetetaan ennen kuin mitään kirjataan. */}
              <div className="liitos-momentti">{nm(tila.seuraava.vaadittu_nm)}</div>

              <textarea
                className="liitos-huomio"
                placeholder={t("la.huomio")}
                value={huomio}
                onChange={(e) => setHuomio(e.target.value)}
                rows={2}
              />
              <button
                className="kuittaa"
                disabled={kirjaa}
                onClick={() => void kuittaa(tila.seuraava!.id)}
              >
                {kirjaa ? t("hl.kirjataan") : t("la.kuittaa")}
              </button>
              <p className="kuittaaja-vihje">
                {t("la.kuittaaja", { nimi: kayttaja.nimi })}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
